import React, { useState, useEffect } from 'react';
import PageLoader from '../components/PageLoader';
import LoadMoreButton from '../components/LoadMoreButton';
import { useNavigate } from 'react-router-dom';
import { Users, Mail, Check } from 'lucide-react';
import { authConfig } from '../config/auth';
import { useAuth } from '../contexts/AuthContext';
import { usePaginatedFetch } from '../hooks/usePaginatedFetch';
import RemoveEmployeeModal from '../components/RemoveEmployeeModal';
import '../styles/employees.css';

interface Employee {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  profilePicture: string | null;
  status: string;
  createdAt: string;
}

type SubmissionStats = {
  total: number;
  approved: number;
  pending: number;
  rejected: number;
};

export default function EmployeesPage() {
  const { token } = useAuth();
  const [submissionStats, setSubmissionStats] = useState<Record<string, SubmissionStats>>({});
  const [employeeToRemove, setEmployeeToRemove] = useState<Employee | null>(null);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [removeSuccess, setRemoveSuccess] = useState(false);
  const navigate = useNavigate();

  const {
    items: employees,
    loading,
    loadingMore,
    hasMore,
    error,
    loadMore,
    refresh,
  } = usePaginatedFetch<Employee>({
    url: `${authConfig.apiUrl}/api/admin/employees`,
    token,
    dataKey: 'employees',
    limit: 50,
  });

  // Fetch submission stats for any employees not yet in the cache
  useEffect(() => {
    if (!token || employees.length === 0) return;
    const missing = employees.filter((e) => !submissionStats[e.userId]);
    if (missing.length === 0) return;

    fetch(`${authConfig.apiUrl}/api/admin/submissions/user-stats`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ userIds: missing.map((e) => e.userId) }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.stats) setSubmissionStats((prev) => ({ ...prev, ...data.stats }));
      })
      .catch(() => {});
  }, [employees]);

  const handleRemove = async () => {
    if (!employeeToRemove || !token) return;

    setRemoving(true);
    setRemoveError(null);

    try {
      const response = await fetch(
        `${authConfig.apiUrl}/api/admin/employees/${employeeToRemove.userId}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.message || 'Failed to remove employee');
      }

      setRemoving(false);
      setRemoveSuccess(true);
      setTimeout(() => {
        setEmployeeToRemove(null);
        setRemoveSuccess(false);
        refresh();
      }, 1300);
    } catch (err: any) {
      setRemoveError(err.message || 'Failed to remove employee');
      setRemoving(false);
    }
  };

  if (loading) {
    return (
      <div className="employees-container">
        <PageLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="employees-container">
        <div className="employees-error">Error: {error}</div>
      </div>
    );
  }

  return (
    <div className="employees-container">
      <div className="employees-header">
        <h1 className="employees-title">Team Members</h1>
        <p className="employees-subtitle">
          {employees.length} {employees.length === 1 ? 'employee' : 'employees'} in your company
        </p>
      </div>

      {employees.length === 0 ? (
        <div className="employees-empty">
          <div className="employees-empty-icon"><Users size={48} /></div>
          <h2 className="employees-empty-title">No employees yet</h2>
          <p className="employees-empty-text">
            Invite team members to get started
          </p>
        </div>
      ) : (
        <div className="employees-grid">
          {employees.map((employee) => (
            <div key={employee.userId} className="employee-card">
              <div className="employee-avatar">
                {employee.profilePicture ? (
                  <img 
                    src={employee.profilePicture} 
                    alt={`${employee.firstName} ${employee.lastName}`}
                    className="employee-avatar-img"
                  />
                ) : (
                  <div className="employee-avatar-placeholder">
                    {employee.firstName?.[0]?.toUpperCase() || '?'}
                    {employee.lastName?.[0]?.toUpperCase() || ''}
                  </div>
                )}
              </div>
              
              <div className="employee-info">
                <h3 className="employee-name">
                  {employee.firstName || 'Unknown'} {employee.lastName || 'User'}
                </h3>
                <p className="employee-email">{employee.email}</p>
              </div>

              <div className="employee-stats">
                {(() => {
                  const stats = submissionStats[employee.userId];
                  const submissionsTotal = stats?.total ?? 0;
                  const approved = stats?.approved ?? 0;
                  const pending = stats?.pending ?? 0;
                  const rejected = stats?.rejected ?? 0;

                  return (
                    <>
                      <div className="employee-submissions-feature">
                        <div className="employee-submissions-feature-value">{submissionsTotal}</div>
                        <div className="employee-submissions-feature-label">Submissions</div>
                        <div className="employee-submissions-feature-meta">
                          Approved: {approved} · Pending: {pending} · Rejected: {rejected}
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>

              <div className="employee-actions">
                <button
                  className="employee-view-btn"
                  onClick={() => navigate(`/employees/${employee.userId}`)}
                >
                  View Profile
                </button>
                <button
                  className="employee-remove-btn"
                  onClick={() => {
                    setRemoveError(null);
                    setEmployeeToRemove(employee);
                  }}
                >
                  Remove
                </button>
              </div>

              <div className="employee-status">
                <span className={`employee-status-badge ${employee.status}`}>
                  {employee.status === 'invited' ? <><Mail size={14} /> Invited</> : <><Check size={14} /> Active</>}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
      <LoadMoreButton onClick={loadMore} loading={loadingMore} hasMore={hasMore} />

      {employeeToRemove && (
        <RemoveEmployeeModal
          name={
            `${employeeToRemove.firstName || ''} ${employeeToRemove.lastName || ''}`.trim() ||
            employeeToRemove.email
          }
          status={employeeToRemove.status}
          loading={removing}
          success={removeSuccess}
          error={removeError}
          onConfirm={handleRemove}
          onClose={() => setEmployeeToRemove(null)}
        />
      )}
    </div>
  );
}

